import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './Boarding.module.scss';
import Button from 'components/UI/V3/Button/Button';
import { Boarding as BoardingModel } from 'model/NewMultipleBooking';
import type { PetServiceWrapper } from 'model/NewMultipleBooking';
import PeriodSelector from './UI/PeriodSelector';
import { Moment } from 'moment-timezone';
import BoardingServiceSelector from './UI/BoardingServiceSelector';
import { BoardingService, BoardingServiceWrapper } from 'model/Service';
import useNonInitialEffect from '@versiondos/hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { RecentlyBooked } from '../UI/RecentlyBooked';
import { AppointmentHistoryDrawer } from '../UI/AppointmentHistoryDrawer';
import OrderDetail from 'components/OrderDetail';
import { AppointmentListItem } from '@spike/appointments-list-model';
import {
    getBoardingCatalogNightlyPrice,
    resolveBoardingStandardRateSelection,
    preserveRoomUnitIdAfterRoomSync,
    syncBoardingRoomsFromOffersCatalog
} from './utils/boardingPricing';
import {
    boardingDealStillAvailable,
    getFirstBoardingDealInCatalog,
    isBoardingDealOfferType
} from './utils/boardingDealOffers';
import { resolveBoardingNightlyPriceFromService } from '../utils/membershipDisplayPrice';
import type { Enrollment, MembershipItem } from '@spike/membership-model';
import { resolveEnrollmentForSave, type EnrollmentAnchor } from '../utils/enrollmentAnchor';
import { PetCustomPrice } from '@spike/pet-model';
import { fetchPetCustomPricesThunk } from '@spike/pets-action';
import { useApiClientWrapper } from 'hooks';
import { resolveStandardPrice } from '../utils/bookingCustomPrices';

function nightsFromBoardingDates(from: Moment, to: Moment): number {
    return Math.max(0, to.clone().startOf('day').diff(from.clone().startOf('day'), 'days'));
}

interface BoardingProps {
    clientId: number;
    petId: number;
    boarding: BoardingModel;
    nights?: number;
    edit: boolean;
    servicesUsed?: Array<number>;
    enrollment?: Enrollment;
    enrollmentAnchor?: EnrollmentAnchor;
    membershipItem?: MembershipItem;
    boardingMembershipAlertServiceId?: number;
    onNext: (boarding: BoardingModel) => void;
    onBoardingServiceChange?: (service: PetServiceWrapper) => void;
    onBoardingPeriodChange?: (from: Moment, to: Moment) => void;
    onClearMembership?: () => void;
    onRoomChanged?: () => void;
}

export const Boarding: FunctionComponent<BoardingProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const boardingServices = useSelector<RootState, Array<BoardingService>>(
        state => state.services.boardingServices || []
    );
    const boardingServicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const customPrices = useSelector<RootState, Array<PetCustomPrice>>(state => state.pets.customPrices);

    const [boardingData, setBoardingData] = useState<BoardingModel>(props.boarding);
    const didUserChangePeriodRef = useRef(false);
    const didUserChangeRoomRef = useRef(false);
    const didApplyInitialBoardingFromPropsRef = useRef(
        props.boarding.service != null || props.boarding.roomUnitId != null
    );
    const [suppressMembershipPropsSync, setSuppressMembershipPropsSync] = useState(false);

    const clearMembershipAndNotifyParent = useCallback(() => {
        setSuppressMembershipPropsSync(true);
        props.onClearMembership?.();
    }, [props.onClearMembership]);

    useEffect(() => {
        if ((props.boarding.service?.membershipItem ?? props.membershipItem) == null) {
            setSuppressMembershipPropsSync(false);
        }
    }, [props.boarding.service?.membershipItem, props.membershipItem]);

    const activeMembershipItem = suppressMembershipPropsSync
        ? boardingData.service?.membershipItem
        : boardingData.service?.membershipItem ?? props.membershipItem;
    const overlayCustomNightly = useCallback(
        (serviceId: number | undefined, nightlyPrice: number, useSavedCustomPrice?: boolean) => {
            if (!serviceId) {
                return { price: nightlyPrice, customPriceId: undefined as number | undefined };
            }
            const resolved = resolveStandardPrice(nightlyPrice, customPrices || [], serviceId);
            if (useSavedCustomPrice === false) {
                return { price: nightlyPrice, customPriceId: resolved.customPriceId };
            }
            return resolved;
        },
        [customPrices]
    );

    useEffect(() => {
        if (props.petId) {
            dispatch(fetchPetCustomPricesThunk(apiClientWrapper, props.petId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.petId]);
    const [showAppointmentHistoryDrawer, setShowAppointmentHistoryDrawer] = useState(false);
    const [recentlyBookedRefreshKey, setRecentlyBookedRefreshKey] = useState(0);
    const [showOrderDetail, setShowOrderDetail] = useState<{ orderId: number; bookingId: number } | null>(null);

    const nightsFromPeriod = useMemo(() => {
        if (!boardingData.from || !boardingData.to) {
            return props.nights ?? 1;
        }
        return nightsFromBoardingDates(boardingData.from, boardingData.to);
    }, [boardingData.from, boardingData.to, props.nights]);

    useEffect(() => {
        if (nightsFromPeriod <= 1) return;
        setBoardingData(prev => {
            if (prev.enrollment?.id == null) return prev;
            return { ...prev, enrollment: undefined };
        });
    }, [nightsFromPeriod]);

    const buildPetServiceWrapperFromBoarding = useCallback(
        (boardingService: BoardingServiceWrapper, price: number, prev: BoardingModel): PetServiceWrapper => {
            const wrapper: PetServiceWrapper = {
                service: {
                    id: boardingService.serviceId,
                    name: boardingService.serviceName ?? 'Boarding Service'
                },
                price,
                beforePrice: boardingService.beforePrice ?? boardingService.price,
                durationInMinutes: boardingService.duration ?? 0,
                duration: boardingService.durationService ?? {
                    hours: Math.trunc((boardingService.duration ?? 0) / 60),
                    minutes: (boardingService.duration ?? 0) % 60
                },
                businessArea: 'boarding',
                color: boardingService.serviceColor ?? '',
                room: boardingService.rooms?.[0] ?? prev.room,
                roomUnitId: prev.roomUnitId,
                notes: prev.notes,
                saveCustomPrice: prev.saveCustomPrice,
                customPriceId: prev.customPriceId,
                customPriceAmount: Number(boardingService.beforePrice) > 0 ? boardingService.beforePrice : undefined
            };
            if (boardingService.selectedOffer != null) {
                wrapper.selectedOffer = boardingService.selectedOffer;
                wrapper.enrollment = undefined;
                wrapper.membershipItem = undefined;
            } else {
                const enrollment = boardingService.enrollment ?? prev.enrollment;
                const membershipItem = boardingService.membershipItem;
                if (enrollment != null) {
                    wrapper.enrollment = enrollment;
                }
                if (membershipItem != null) {
                    wrapper.membershipItem = membershipItem;
                }
            }
            return wrapper;
        },
        []
    );

    const clearStaleBoardingDeal = useCallback(
        (prev: BoardingModel): BoardingModel | null => {
            if (props.edit) {
                return null;
            }
            const svc = prev.service;
            const deal = svc?.selectedOffer;
            if (svc?.serviceId == null || deal == null || !isBoardingDealOfferType(deal)) {
                return null;
            }
            if (boardingDealStillAvailable(boardingServices, svc.serviceId, deal)) {
                return null;
            }
            const firstDeal = getFirstBoardingDealInCatalog(boardingServices, svc.serviceId);
            const roomSync = syncBoardingRoomsFromOffersCatalog(svc, boardingServices, prev.room);
            const room =
                roomSync?.room ?? boardingServices.find(s => s.serviceId === svc.serviceId)?.rooms?.[0] ?? prev.room;

            if (firstDeal) {
                const catalogService = boardingServices.find(s => s.serviceId === firstDeal.serviceId);
                const nextService: BoardingServiceWrapper = {
                    ...(catalogService ?? svc),
                    ...svc,
                    serviceId: firstDeal.serviceId,
                    beforePrice: svc.beforePrice ?? svc.price,
                    price: firstDeal.offer.pricePerNight,
                    selectedOffer: firstDeal.offer,
                    membershipItem: undefined,
                    enrollment: undefined,
                    rooms: roomSync?.service.rooms ?? catalogService?.rooms ?? svc.rooms
                };
                return {
                    ...prev,
                    enrollment: undefined,
                    service: nextService,
                    price: firstDeal.offer.pricePerNight,
                    room,
                    roomUnitId: preserveRoomUnitIdAfterRoomSync(roomSync, prev.roomUnitId, room)
                };
            }

            const { service: nextService, nightlyPrice } = resolveBoardingStandardRateSelection(
                { ...svc, selectedOffer: undefined },
                boardingServices
            );
            return {
                ...prev,
                enrollment: undefined,
                service: { ...nextService, selectedOffer: undefined },
                price: nightlyPrice,
                room,
                roomUnitId: preserveRoomUnitIdAfterRoomSync(roomSync, prev.roomUnitId, room)
            };
        },
        [boardingServices, props.edit]
    );

    const syncBoardingFromOffersCatalog = useCallback(
        (prev: BoardingModel): BoardingModel | null => {
            const svc = prev.service;
            if (svc?.serviceId == null || svc.selectedOffer != null) {
                return null;
            }

            const roomSync = syncBoardingRoomsFromOffersCatalog(svc, boardingServices, prev.room);
            if (props.edit) {
                if (!roomSync) {
                    return null;
                }
                const room = roomSync.room;
                const roomUnitId = preserveRoomUnitIdAfterRoomSync(roomSync, prev.roomUnitId, room);
                if (
                    prev.room === room &&
                    prev.roomUnitId === roomUnitId &&
                    prev.service?.rooms === roomSync.service.rooms
                ) {
                    return null;
                }
                return {
                    ...prev,
                    price: prev.price,
                    service: { ...svc, rooms: roomSync.service.rooms, price: prev.price },
                    room,
                    roomUnitId
                };
            }
            const membershipItem = svc.membershipItem ?? props.membershipItem;
            const enrollment = prev.enrollment ?? svc.enrollment ?? props.enrollment;
            const hasMembership = membershipItem != null || enrollment?.id != null;

            let nextService: BoardingServiceWrapper;
            let nightlyPrice: number;
            let customPriceId: number | undefined = prev.customPriceId;
            const room =
                roomSync?.room ?? boardingServices.find(s => s.serviceId === svc.serviceId)?.rooms?.[0] ?? prev.room;

            if (hasMembership) {
                const latest = boardingServices.find(s => s.serviceId === svc.serviceId);
                const mergedService: BoardingServiceWrapper = {
                    ...svc,
                    ...(latest ?? {}),
                    rooms: roomSync?.service.rooms ?? latest?.rooms ?? svc.rooms,
                    membershipItem,
                    enrollment
                };
                const catalogNightly = getBoardingCatalogNightlyPrice(latest ?? mergedService);
                const overlaid = overlayCustomNightly(svc.serviceId, catalogNightly, prev.useSavedCustomPrice);
                nightlyPrice = resolveBoardingNightlyPriceFromService(mergedService, membershipItem, overlaid.price);
                customPriceId = overlaid.customPriceId;
                nextService = {
                    ...mergedService,
                    catalogPrice: mergedService.catalogPrice ?? catalogNightly,
                    beforePrice: overlaid.price,
                    price: nightlyPrice
                };
            } else {
                const resolved = resolveBoardingStandardRateSelection(svc, boardingServices);
                nextService = resolved.service;
                const overlaid = overlayCustomNightly(svc.serviceId, resolved.nightlyPrice, prev.useSavedCustomPrice);
                nightlyPrice = overlaid.price;
                customPriceId = overlaid.customPriceId;
            }

            const roomUnitId = preserveRoomUnitIdAfterRoomSync(roomSync, prev.roomUnitId, room);
            if (
                Math.abs(prev.price - nightlyPrice) < 0.004 &&
                prev.service?.offers === nextService.offers &&
                prev.service?.appliedOffer === nextService.appliedOffer &&
                prev.room === room &&
                prev.roomUnitId === roomUnitId &&
                prev.customPriceId === customPriceId
            ) {
                return null;
            }

            return {
                ...prev,
                price: nightlyPrice,
                customPriceId,
                service: nextService,
                room,
                roomUnitId,
                ...(hasMembership && enrollment != null ? { enrollment } : {})
            };
        },
        [boardingServices, props.edit, props.membershipItem, props.enrollment, overlayCustomNightly]
    );

    const applyOffersCatalogSync = useCallback(() => {
        setBoardingData(prev => {
            const withoutStaleDeal = clearStaleBoardingDeal(prev);
            const base = withoutStaleDeal ?? prev;
            const next = syncBoardingFromOffersCatalog(base);
            if (!next) {
                if (withoutStaleDeal && withoutStaleDeal.service?.serviceId != null) {
                    props.onBoardingServiceChange?.(
                        buildPetServiceWrapperFromBoarding(
                            withoutStaleDeal.service,
                            withoutStaleDeal.price,
                            withoutStaleDeal
                        )
                    );
                }
                return withoutStaleDeal ?? prev;
            }
            if (next.service?.serviceId != null) {
                props.onBoardingServiceChange?.(buildPetServiceWrapperFromBoarding(next.service, next.price, next));
            }
            return next;
        });
    }, [
        clearStaleBoardingDeal,
        syncBoardingFromOffersCatalog,
        props.onBoardingServiceChange,
        buildPetServiceWrapperFromBoarding
    ]);

    useEffect(() => {
        applyOffersCatalogSync();
    }, [boardingData.from, boardingData.to, boardingData.service?.serviceId, boardingServices, applyOffersCatalogSync]);

    const changePeriodHandler = (from: Moment, to: Moment, _nightsFromPicker?: number) => {
        let newFrom = from.clone();
        let newTo = to.clone();

        const prevFrom = boardingData.from.clone();
        const prevTo = boardingData.to.clone();

        const dayFrom = newFrom.clone().startOf('day');
        const dayTo = newTo.clone().startOf('day');

        const dayPrevFrom = prevFrom.clone().startOf('day');
        const dayPrevTo = prevTo.clone().startOf('day');

        const fromChanged = !dayFrom.isSame(dayPrevFrom, 'day');
        const toChanged = !dayTo.isSame(dayPrevTo, 'day');

        if (fromChanged) {
            if (dayFrom.isSame(dayTo)) {
                newTo = newTo.clone().add(1, 'day');
            }

            if (dayFrom.isAfter(dayTo)) {
                newTo = newFrom.clone().add(1, 'day');
            }
        }

        if (toChanged) {
            if (dayTo.isSame(dayFrom)) {
                newFrom = newFrom.clone().subtract(1, 'day');
            }

            if (dayTo.isBefore(dayFrom)) {
                newFrom = newTo.clone().subtract(1, 'day');
            }
        }

        if (dayFrom.isSame(dayTo)) {
            newTo = newFrom.clone().add(1, 'day');
        }

        const datesChanged = fromChanged || toChanged;

        if (datesChanged) {
            didUserChangePeriodRef.current = true;
        }

        const currentService = boardingData.service;
        const shouldApplyStandardAfterDateChange =
            !props.edit && datesChanged && currentService?.serviceId != null && currentService.selectedOffer == null;

        let standardWrapperAfterDateChange: PetServiceWrapper | undefined;
        if (shouldApplyStandardAfterDateChange && currentService != null) {
            const { service: nextService, nightlyPrice } = resolveBoardingStandardRateSelection(
                currentService,
                boardingServices
            );
            standardWrapperAfterDateChange = buildPetServiceWrapperFromBoarding(
                nextService,
                nightlyPrice,
                boardingData
            );
        }

        setBoardingData(prev => {
            const nextFromTo = {
                ...prev,
                from: newFrom,
                to: newTo,
                ...(datesChanged ? { roomUnitId: undefined } : {})
            };
            if (!datesChanged || prev.service?.serviceId == null || prev.service.selectedOffer != null) {
                return nextFromTo;
            }
            if (props.edit) {
                return nextFromTo;
            }
            const { service: nextService, nightlyPrice } = resolveBoardingStandardRateSelection(
                prev.service,
                boardingServices
            );
            const room =
                boardingServices.find(s => s.serviceId === prev.service?.serviceId)?.rooms?.[0] ??
                nextService.rooms?.[0];
            return {
                ...nextFromTo,
                enrollment: undefined,
                price: nightlyPrice,
                service: nextService,
                room
            };
        });

        props.onBoardingPeriodChange?.(newFrom, newTo);

        if (standardWrapperAfterDateChange != null) {
            props.onBoardingServiceChange?.(standardWrapperAfterDateChange);
        }

        if (datesChanged && !props.edit) {
            clearMembershipAndNotifyParent();
        }
    };

    useEffect(() => {
        if (props.edit) return;
        if (suppressMembershipPropsSync) return;

        const parentMembership = props.boarding.service?.membershipItem ?? props.membershipItem;
        if (parentMembership == null) return;

        setBoardingData(prev => {
            if (prev.service?.serviceId == null || prev.service.selectedOffer != null) {
                if (prev.service?.selectedOffer != null) {
                    return prev;
                }
                return {
                    ...props.boarding,
                    from: prev.from?.isValid() ? prev.from : props.boarding.from,
                    to: prev.to?.isValid() ? prev.to : props.boarding.to
                };
            }
            const membershipItem = prev.service.membershipItem ?? parentMembership;
            const catalogNightly = getBoardingCatalogNightlyPrice(prev.service);
            const overlaid = overlayCustomNightly(prev.service.serviceId, catalogNightly, prev.useSavedCustomPrice);
            const service: BoardingServiceWrapper = {
                ...prev.service,
                membershipItem,
                beforePrice: overlaid.price,
                catalogPrice: prev.service.catalogPrice ?? catalogNightly
            };
            if (service.selectedOffer != null) {
                return { ...prev, service };
            }
            const nightly = resolveBoardingNightlyPriceFromService(service, membershipItem, overlaid.price);
            if (
                prev.service.membershipItem === membershipItem &&
                Math.abs(prev.price - nightly) < 0.004 &&
                prev.customPriceId === overlaid.customPriceId
            ) {
                return prev;
            }
            return { ...prev, service, price: nightly, customPriceId: overlaid.customPriceId };
        });
    }, [
        props.boarding.service?.serviceId,
        props.boarding.service?.membershipItem,
        props.membershipItem,
        overlayCustomNightly,
        suppressMembershipPropsSync,
        props.edit
    ]);

    const changeSelectHandler = (service: BoardingServiceWrapper) => {
        const selectedDeal = service.selectedOffer;
        if (selectedDeal != null) {
            setBoardingData(prev => {
                const nextService: BoardingServiceWrapper = {
                    ...service,
                    beforePrice: service.beforePrice ?? service.price,
                    price: selectedDeal.pricePerNight,
                    selectedOffer: selectedDeal,
                    membershipItem: undefined,
                    enrollment: undefined
                };
                const next: BoardingModel = {
                    ...prev,
                    enrollment: undefined,
                    service: nextService,
                    price: selectedDeal.pricePerNight,
                    room: service.rooms?.[0] ?? prev.room,
                    roomUnitId: prev.roomUnitId,
                    notes: prev.notes,
                    saveCustomPrice: false,
                    customPriceId: undefined
                };
                props.onBoardingServiceChange?.(
                    buildPetServiceWrapperFromBoarding(nextService, selectedDeal.pricePerNight, prev)
                );
                return next;
            });
            return;
        }

        const serviceChanged =
            boardingData.service?.serviceId != null &&
            service.serviceId != null &&
            boardingData.service.serviceId !== service.serviceId;

        const comingFromDeal = boardingData.service?.selectedOffer != null;
        const explicitMembershipClearFromDealsTab =
            Object.prototype.hasOwnProperty.call(service, 'membershipItem') &&
            Object.prototype.hasOwnProperty.call(service, 'enrollment') &&
            service.membershipItem === undefined &&
            service.enrollment === undefined;
        const useStandardRatesOnly = comingFromDeal || explicitMembershipClearFromDealsTab || serviceChanged;

        if (useStandardRatesOnly) {
            if (serviceChanged) {
                clearMembershipAndNotifyParent();
            } else {
                setSuppressMembershipPropsSync(true);
            }
        }

        const durationMinutes =
            boardingServices.find(s => s.serviceId === service.serviceId)?.duration ?? service.duration ?? 0;
        const serviceForPricing: BoardingServiceWrapper = {
            ...service,
            durationService: service.durationService ?? {
                hours: Math.trunc(durationMinutes / 60),
                minutes: durationMinutes % 60
            },
            selectedOffer: undefined,
            membershipItem: undefined,
            enrollment: undefined
        };

        let nextService: BoardingServiceWrapper;
        let nightlyPrice: number;
        let customPriceId: number | undefined;

        if (useStandardRatesOnly) {
            const resolved = resolveBoardingStandardRateSelection(serviceForPricing, boardingServices);
            nextService = resolved.service;
            const overlaid = overlayCustomNightly(resolved.service.serviceId, resolved.nightlyPrice);
            nightlyPrice = overlaid.price;
            customPriceId = overlaid.customPriceId;
        } else {
            const membershipItem =
                service.membershipItem ?? boardingData.service?.membershipItem ?? props.membershipItem;
            const enrollment =
                service.enrollment ?? boardingData.enrollment ?? boardingData.service?.enrollment ?? props.enrollment;
            const resolved = resolveBoardingStandardRateSelection(
                { ...serviceForPricing, membershipItem, enrollment },
                boardingServices
            );
            const catalogNightly = getBoardingCatalogNightlyPrice(resolved.service);
            const overlaid = overlayCustomNightly(resolved.service.serviceId, catalogNightly);
            nightlyPrice = resolveBoardingNightlyPriceFromService(
                { ...resolved.service, membershipItem, enrollment },
                membershipItem,
                overlaid.price
            );
            customPriceId = overlaid.customPriceId;
            nextService = {
                ...resolved.service,
                membershipItem,
                enrollment,
                catalogPrice: resolved.service.catalogPrice ?? catalogNightly,
                beforePrice: overlaid.price,
                price: nightlyPrice
            };
        }

        setBoardingData(prev => {
            const changedService =
                prev.service?.serviceId != null &&
                service.serviceId != null &&
                prev.service.serviceId !== service.serviceId;
            return {
                ...prev,
                enrollment: useStandardRatesOnly ? undefined : nextService.enrollment,
                service: nextService,
                price: nightlyPrice,
                customPriceId,
                saveCustomPrice: false,
                useSavedCustomPrice: changedService ? undefined : prev.useSavedCustomPrice,
                room: service.rooms?.[0] ?? prev.room,
                roomUnitId: changedService ? undefined : prev.roomUnitId,
                notes: prev.notes
            };
        });

        if (useStandardRatesOnly || serviceChanged) {
            props.onBoardingServiceChange?.(
                buildPetServiceWrapperFromBoarding(nextService, nightlyPrice, boardingData)
            );
        }
    };

    const changeSelectRoomUnitIdHandler = useCallback(
        (roomUnitId: number | undefined) => {
            setBoardingData(prev => {
                if (prev.roomUnitId === roomUnitId) {
                    return prev;
                }

                didUserChangeRoomRef.current = true;
                props.onRoomChanged?.();

                return {
                    ...prev,
                    roomUnitId: roomUnitId
                };
            });
        },
        [props.onRoomChanged]
    );

    const changeNotesHandler = useCallback((notes: string) => {
        setBoardingData(prev => ({ ...prev, notes }));
    }, []);

    const changeNightlyPriceHandler = useCallback((price: number) => {
        setBoardingData(prev => ({
            ...prev,
            price
        }));
    }, []);

    const changeSaveCustomPriceHandler = useCallback((checked: boolean) => {
        setBoardingData(prev => ({ ...prev, saveCustomPrice: checked }));
    }, []);

    const changeUseSavedCustomPriceHandler = useCallback((useSaved: boolean, price: number, customPriceId?: number) => {
        setBoardingData(prev => ({
            ...prev,
            useSavedCustomPrice: useSaved,
            price,
            customPriceId: customPriceId ?? prev.customPriceId
        }));
    }, []);

    useEffect(() => {
        if (didApplyInitialBoardingFromPropsRef.current) {
            return;
        }
        if (props.boarding.service == null && props.boarding.roomUnitId == null) {
            return;
        }
        setBoardingData(prev => {
            const incoming = props.boarding;
            if (props.edit) {
                return incoming;
            }
            const shouldPreserveLocalPeriod =
                didUserChangePeriodRef.current &&
                prev.from?.isValid() &&
                incoming.from?.isValid() &&
                !prev.from.isSame(incoming.from, 'day');
            if (shouldPreserveLocalPeriod) {
                return {
                    ...incoming,
                    from: prev.from,
                    to: prev.to?.isValid() ? prev.to : incoming.to
                };
            }
            return incoming;
        });
        didApplyInitialBoardingFromPropsRef.current = true;
    }, [props.boarding.service?.serviceId, props.boarding.roomUnitId]);

    useEffect(() => {
        if (props.edit) return;
        if (didUserChangePeriodRef.current) return;
        if (!props.boarding.from?.isValid() || !props.boarding.to?.isValid()) return;

        setBoardingData(prev => {
            const fromSame = prev.from?.isValid() && prev.from.isSame(props.boarding.from, 'day');
            const toSame = prev.to?.isValid() && prev.to.isSame(props.boarding.to, 'day');
            if (fromSame && toSame) {
                return prev;
            }
            return {
                ...prev,
                from: props.boarding.from.clone(),
                to: props.boarding.to.clone()
            };
        });
    }, [props.edit, props.boarding.from?.format('YYYY-MM-DD'), props.boarding.to?.format('YYYY-MM-DD')]);

    useEffect(() => {
        if (props.edit && props.boarding.from && props.boarding.to) {
            setBoardingData(prev => {
                const fromChanged = !prev.from || !prev.from.isSame(props.boarding.from, 'day');
                const toChanged = !prev.to || !prev.to.isSame(props.boarding.to, 'day');

                if (fromChanged || toChanged) {
                    return {
                        ...prev,
                        from: props.boarding.from.clone(),
                        to: props.boarding.to.clone()
                    };
                }
                return prev;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.edit, props.boarding.from?.format('YYYY-MM-DD HH:mm'), props.boarding.to?.format('YYYY-MM-DD HH:mm')]);

    useNonInitialEffect(() => {
        if (boardingServicesStatus !== ServicesStatus.FetchBoardingOffersSuccess) {
            return;
        }
        applyOffersCatalogSync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boardingServicesStatus, boardingServices]);

    const isNightlyPriceInvalid = !Number.isFinite(boardingData.price) || boardingData.price < 0;

    const nightlyPriceError = isNightlyPriceInvalid
        ? 'Enter a valid nightly price greater than or equal to $0.00.'
        : undefined;

    return (
        <>
            <div className={styles.boardingSection}>
                <PeriodSelector
                    from={boardingData.from}
                    to={boardingData.to}
                    labelTimeStart="Check-In"
                    labelTimeEnd="Check-Out"
                    onChangePeriod={changePeriodHandler}
                />
                <div className={styles.recentlyBookedSection}>
                    <RecentlyBooked
                        clientId={props.clientId}
                        petId={props.petId}
                        refreshTrigger={recentlyBookedRefreshKey}
                        onViewAll={() => setShowAppointmentHistoryDrawer(true)}
                        onAppointmentClick={(appointment: AppointmentListItem) => {
                            if (appointment.orderId != null && appointment.bookingId != null) {
                                setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                            }
                        }}
                    />
                </div>
                <BoardingServiceSelector
                    petId={props.petId}
                    from={boardingData.from}
                    to={boardingData.to}
                    nights={nightsFromPeriod}
                    edit={props.edit}
                    room={boardingData.room}
                    selectedService={boardingData.service}
                    selectedRoomUnitId={boardingData.roomUnitId}
                    nightlyPrice={boardingData.price}
                    nightlyPriceError={nightlyPriceError}
                    notes={boardingData.notes}
                    saveCustomPrice={boardingData.saveCustomPrice}
                    useSavedCustomPrice={boardingData.useSavedCustomPrice}
                    membershipItem={activeMembershipItem}
                    boardingMembershipAlertServiceId={props.boardingMembershipAlertServiceId}
                    onChangePeriod={changePeriodHandler}
                    onSelectService={changeSelectHandler}
                    onNightlyPriceChange={changeNightlyPriceHandler}
                    onSaveCustomPriceChange={changeSaveCustomPriceHandler}
                    onUseSavedCustomPriceChange={changeUseSavedCustomPriceHandler}
                    onSelectRoomUnitId={changeSelectRoomUnitIdHandler}
                    onNotesChange={changeNotesHandler}
                />
                <div className={styles.footerContainer}>
                    <Button
                        className={styles.buttonSave}
                        disabled={!boardingData?.service || isNightlyPriceInvalid}
                        variant="green"
                        label="Next"
                        size="large"
                        onClick={() => {
                            const dateKey = boardingData.from.format('YYYY-MM-DD');
                            let e = resolveEnrollmentForSave(
                                props.enrollment,
                                props.enrollmentAnchor ?? null,
                                dateKey,
                                boardingData.service?.serviceId
                            );
                            if (nightsFromPeriod > 1) {
                                e = undefined;
                            }
                            const payload: BoardingModel = { ...boardingData };
                            if (boardingData.service?.selectedOffer != null) {
                                delete (payload as { enrollment?: unknown }).enrollment;
                            } else if (e != null) {
                                payload.enrollment = e;
                            } else {
                                delete (payload as { enrollment?: unknown }).enrollment;
                            }
                            props.onNext(payload);
                        }}
                    />
                </div>
            </div>
            <AppointmentHistoryDrawer
                open={showAppointmentHistoryDrawer}
                onBack={() => {
                    setShowAppointmentHistoryDrawer(false);
                    setRecentlyBookedRefreshKey(k => k + 1);
                }}
                customerId={props.clientId}
                petId={props.petId}
                onAppointmentClick={(appointment: AppointmentListItem) => {
                    if (appointment.orderId != null && appointment.bookingId != null) {
                        setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                    }
                }}
            />
            {showOrderDetail && (
                <OrderDetail
                    orderId={showOrderDetail.orderId}
                    bookingId={showOrderDetail.bookingId}
                    readOnly={true}
                    onClose={() => setShowOrderDetail(null)}
                    zIndex={10100}
                />
            )}
        </>
    );
};

export default Boarding;
